<template>
  <UiPanel class="encounter-surface" tone="combat" raised role="region" aria-label="Encounter">
    <div class="header">
      <div class="title">
        <span class="icon" aria-hidden="true">{{ ICONS.domains.combat }}</span>
        <div style="min-width: 0;">
          <div class="title-text">Encounter</div>
          <div class="subtitle text-muted">
            <template v-if="encounter">
              Turn {{ formatNumber(encounter.turnNumber ?? 1) }} · {{ encounter.type }} · {{ encounter.areaId }}
            </template>
            <template v-else>Loading…</template>
          </div>
        </div>
      </div>

      <div class="header-right">
        <UiChip v-if="isPlayerTurn" :variant="timerVariant" class="stat-mono num">
          {{ ICONS.combat.melee }} {{ timerLabel }}
        </UiChip>
        <UiChip v-else variant="muted" class="stat-mono num">Waiting…</UiChip>

        <UiButton
          v-if="canDismiss"
          size="sm"
          variant="secondary"
          @click="dismiss"
        >
          <template #icon>{{ ICONS.actions.close }}</template>
          Dismiss
        </UiButton>
        <UiChip v-else variant="warning">Combat Active</UiChip>
      </div>
    </div>

    <UiCallout v-if="encounter && canDismiss" variant="success" class="resolution-callout">
      Combat is resolved. Review the log and dismiss to return to the world.
    </UiCallout>

    <EncounterPanel
      v-if="encounter"
      embedded
      :encounter="encounter"
      :playerId="playerId"
      :readiedSpells="readiedSpells"
      :turnTimeRemaining="turnTimeRemaining"
      :localLogLines="localLogLines"
      :consumables="consumables"
      :canUseConsumable="canUseConsumable"
      :useDisabledReason="useDisabledReason || undefined"
      :usingItemId="usingItemId"
      :bonusActionUsed="bonusActionUsed"
      :loadout="loadout"
      :reloadOptions="reloadOptions"
      :canReload="canReload"
      :reloadDisabledReason="reloadDisabledReason || undefined"
      :hotSwapOptions="hotSwapOptions"
      :canHotSwap="canHotSwap"
      :hotSwapDisabledReason="hotSwapDisabledReason || undefined"
      :turnSubmitting="turnSubmitting"
      @dismiss="dismiss"
      @action="(action) => onAction(action)"
      @cast-spell="(id) => onCastSpell(id)"
      @reload="(id) => onReload(id)"
      @hot-swap="(id) => onHotSwap(id)"
      @use-consumable="(id) => onUseConsumable(id)"
    />
  </UiPanel>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { Encounter, InventoryEntry, Spell, TurnAction } from "@idle-rpg/shared";
import { ICONS } from "../../../lib/icons";
import { formatNumber } from "../../../lib/format";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import EncounterPanel from "../../../components/panels/EncounterPanel.vue";

const props = defineProps<{
  encounter: Encounter | null;
  playerId: string;
  readiedSpells: Spell[];
  turnTimeRemaining: number | null;
  localLogLines: string[];
  consumables: InventoryEntry[];
  canUseConsumable: boolean;
  useDisabledReason: string | null;
  usingItemId: string | null;
  bonusActionUsed: boolean;
  loadout: any;
  reloadOptions: any[];
  canReload: boolean;
  reloadDisabledReason: string | null;
  hotSwapOptions: any[];
  canHotSwap: boolean;
  hotSwapDisabledReason: string | null;
  turnSubmitting: boolean;
  onDismiss: () => void;
  onAction: (action: TurnAction) => void;
  onCastSpell: (spellId: string) => void;
  onReload: (itemId: string | null) => void;
  onHotSwap: (instanceId: string) => void;
  onUseConsumable: (itemId: string) => void;
}>();

const canDismiss = computed(() => !!props.encounter && !props.encounter.activeTurn);
const isPlayerTurn = computed(() => {
  if (!props.encounter) return false;
  return props.encounter.activeTurn === props.playerId;
});

const timerLabel = computed(() => {
  if (!isPlayerTurn.value) return "—";
  const t = props.turnTimeRemaining;
  if (t === null) return "—";
  return `${Math.max(0, Math.floor(t))}s`;
});

const timerVariant = computed(() => {
  const t = props.turnTimeRemaining ?? 999;
  if (t <= 5) return "danger";
  if (t <= 12) return "warning";
  return "muted";
});

function dismiss() {
  if (!canDismiss.value) return;
  props.onDismiss();
}
</script>

<style scoped>
.encounter-surface {
  width: 100%;
  min-height: 0;
}

.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}

.title {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  min-width: 0;
}

.icon {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-2);
  display: grid;
  place-items: center;
  background: var(--surface-ghost-bg);
  border: 1px solid color-mix(in srgb, var(--accent-combat) 22%, var(--border-0));
  box-shadow: var(--shadow-0);
  font-size: 1.25rem;
}

.title-text {
  font-family: var(--font-display);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 800;
  line-height: 1.1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.subtitle {
  margin-top: 0.2rem;
  font-size: 0.9rem;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.resolution-callout {
  margin-block: 0.8rem;
  padding-block: 0.95rem;
}
</style>
